import React, { createContext, useContext, useState, useCallback, useMemo } from 'react'
import useSWR from 'swr'
import type {
  Bookmaker,
  FixtureOddsResponse,
  OddsFormat,
  BestOddsInfo,
} from '../types'
import { fetcher, forceRevalidateOdds } from '../swr-config'

// Odds format conversion utilities
function decimalToFractional(decimal: number): string {
  const fraction = decimal - 1

  // For large odds (> 11), just show as X/1
  if (fraction >= 11) {
    return `${Math.round(fraction)}/1`
  }

  const fractions: [number, number][] = [
    [1, 10], [1, 5], [1, 4], [1, 3], [2, 5], [1, 2], [4, 7], [8, 13],
    [4, 6], [8, 11], [5, 6], [10, 11], [1, 1], [6, 5], [5, 4], [11, 8],
    [6, 4], [7, 4], [2, 1], [9, 4], [5, 2], [11, 4], [3, 1], [7, 2],
    [4, 1], [9, 2], [5, 1], [6, 1], [7, 1], [8, 1], [9, 1], [10, 1]
  ]

  let closest = fractions[0]
  let minDiff = Math.abs(fraction - closest[0] / closest[1])

  for (const [num, den] of fractions) {
    const diff = Math.abs(fraction - num / den)
    if (diff < minDiff) {
      minDiff = diff
      closest = [num, den]
    }
  }

  return `${closest[0]}/${closest[1]}`
}

function decimalToAmerican(decimal: number): string {
  if (decimal >= 2) {
    return `+${Math.round((decimal - 1) * 100)}`
  } else {
    return `${Math.round(-100 / (decimal - 1))}`
  }
}

interface OddsContextType {
  // Bookmakers
  bookmakers: Bookmaker[]
  bookmakersLoading: boolean

  // User preference (stored in localStorage for now)
  preferredBookmaker: Bookmaker | null
  setPreferredBookmaker: (slug: string) => void

  // Odds format
  oddsFormat: OddsFormat
  setOddsFormat: (format: OddsFormat) => void

  // Odds visibility toggle
  oddsHidden: boolean
  setOddsHidden: (hidden: boolean) => void

  // Value betting preferences
  showValueBadges: boolean
  setShowValueBadges: (show: boolean) => void
  minEdgeThreshold: number
  setMinEdgeThreshold: (threshold: number) => void

  // Fixture odds (cached)
  getFixtureOddsData: (fixtureId: number) => FixtureOddsResponse | null
  fetchFixtureOddsData: (fixtureId: number) => Promise<void>
  fetchMatchWinnerOdds: (fixtureIds: number[]) => Promise<void>
  fetchFullFixtureOdds: (fixtureId: number) => Promise<void>
  forceRefreshOdds: (fixtureId: number) => Promise<void>
  oddsLoading: Record<number, boolean>

  // Utilities
  formatOdds: (decimal: number) => string
  getBestOddsForSelection: (
    fixtureId: number,
    marketSlug: string,
    outcome: string,
    line?: number | null
  ) => BestOddsInfo | null
  getOddsForBookmaker: (
    fixtureId: number,
    marketSlug: string,
    outcome: string,
    bookmakerSlug: string,
    line?: number | null
  ) => number | null
  // Player market odds lookup
  getBestOddsForPlayer: (
    fixtureId: number,
    marketSlug: string,
    playerName: string
  ) => BestOddsInfo | null
  getOddsForPlayerBookmaker: (
    fixtureId: number,
    marketSlug: string,
    playerName: string,
    bookmakerSlug: string
  ) => number | null
}

const OddsContext = createContext<OddsContextType | null>(null)

const BOOKMAKER_STORAGE_KEY = 'statscout_preferred_bookmaker'
const ODDS_FORMAT_STORAGE_KEY = 'statscout_odds_format'
const ODDS_HIDDEN_STORAGE_KEY = 'statscout_odds_hidden'
const SHOW_VALUE_BADGES_KEY = 'statscout_show_value_badges'
const MIN_EDGE_THRESHOLD_KEY = 'statscout_min_edge_threshold'

// Custom hook for fixture odds with SWR
function useFixtureOddsSWR(fixtureId: number | null) {
  return useSWR<FixtureOddsResponse>(
    fixtureId ? `/odds/fixtures/${fixtureId}` : null,
    fetcher,
    {
      dedupingInterval: 30000, // Odds update frequently
      revalidateOnFocus: true,
    }
  )
}

export function OddsProvider({ children }: { children: React.ReactNode }) {
  // SWR for bookmakers - they rarely change, so longer cache
  const { data: bookmakers = [], isLoading: bookmakersLoading } = useSWR<Bookmaker[]>(
    '/odds/bookmakers',
    fetcher,
    {
      dedupingInterval: 300000, // 5 minutes
      revalidateOnFocus: false,
    }
  )

  // Track which fixture IDs have been requested for odds
  const [activeFixtureId, setActiveFixtureId] = useState<number | null>(null)
  const { data: activeOddsData, isLoading: activeOddsLoading } = useFixtureOddsSWR(activeFixtureId)

  // Cache of odds data by fixture ID (populated from SWR results)
  const [oddsCache, setOddsCache] = useState<Record<number, FixtureOddsResponse>>({})

  // Update cache when SWR returns data
  React.useEffect(() => {
    if (activeFixtureId && activeOddsData) {
      setOddsCache((prev) => ({
        ...prev,
        [activeFixtureId]: activeOddsData,
      }))
    }
  }, [activeFixtureId, activeOddsData])

  // Load preferences from localStorage
  const [preferredBookmakerSlug, setPreferredBookmakerSlug] = useState<string>(() => {
    return localStorage.getItem(BOOKMAKER_STORAGE_KEY) || 'bet365'
  })

  const [oddsFormat, setOddsFormatState] = useState<OddsFormat>(() => {
    const stored = localStorage.getItem(ODDS_FORMAT_STORAGE_KEY)
    return (stored as OddsFormat) || 'decimal'
  })

  const [oddsHidden, setOddsHiddenState] = useState<boolean>(() => {
    const stored = localStorage.getItem(ODDS_HIDDEN_STORAGE_KEY)
    return stored === 'true'
  })

  // Value betting preferences
  const [showValueBadges, setShowValueBadgesState] = useState<boolean>(() => {
    const stored = localStorage.getItem(SHOW_VALUE_BADGES_KEY)
    // Default to true (show value badges)
    return stored !== 'false'
  })

  const [minEdgeThreshold, setMinEdgeThresholdState] = useState<number>(() => {
    const stored = localStorage.getItem(MIN_EDGE_THRESHOLD_KEY)
    return stored ? parseFloat(stored) : 0
  })

  // Derive preferred bookmaker from slug
  const preferredBookmaker = useMemo(() => {
    return bookmakers.find((b) => b.slug === preferredBookmakerSlug) || null
  }, [bookmakers, preferredBookmakerSlug])

  // Set preferred bookmaker
  const setPreferredBookmaker = useCallback((slug: string) => {
    setPreferredBookmakerSlug(slug)
    localStorage.setItem(BOOKMAKER_STORAGE_KEY, slug)
  }, [])

  // Set odds format
  const setOddsFormat = useCallback((format: OddsFormat) => {
    setOddsFormatState(format)
    localStorage.setItem(ODDS_FORMAT_STORAGE_KEY, format)
  }, [])

  // Set odds hidden
  const setOddsHidden = useCallback((hidden: boolean) => {
    setOddsHiddenState(hidden)
    localStorage.setItem(ODDS_HIDDEN_STORAGE_KEY, String(hidden))
  }, [])

  // Set show value badges
  const setShowValueBadges = useCallback((show: boolean) => {
    setShowValueBadgesState(show)
    localStorage.setItem(SHOW_VALUE_BADGES_KEY, String(show))
  }, [])

  // Set minimum edge threshold
  const setMinEdgeThreshold = useCallback((threshold: number) => {
    setMinEdgeThresholdState(threshold)
    localStorage.setItem(MIN_EDGE_THRESHOLD_KEY, String(threshold))
  }, [])

  // Format odds based on user preference
  const formatOdds = useCallback(
    (decimal: number): string => {
      switch (oddsFormat) {
        case 'fractional':
          return decimalToFractional(decimal)
        case 'american':
          return decimalToAmerican(decimal)
        default:
          return decimal.toFixed(2)
      }
    },
    [oddsFormat]
  )

  // Get cached fixture odds (from SWR cache)
  const getFixtureOddsData = useCallback(
    (fixtureId: number): FixtureOddsResponse | null => {
      return oddsCache[fixtureId] ?? null
    },
    [oddsCache]
  )

  // Fetch fixture odds - triggers SWR fetch by setting active fixture
  const fetchFixtureOddsData = useCallback(async (fixtureId: number): Promise<void> => {
    // If already cached, no need to fetch
    if (oddsCache[fixtureId]) return

    // Set active fixture to trigger SWR fetch
    setActiveFixtureId(fixtureId)
  }, [oddsCache])

  // Fetch just match-winner (1X2) odds for multiple fixtures (lightweight, for fixture cards)
  const fetchMatchWinnerOdds = useCallback(async (fixtureIds: number[]): Promise<void> => {
    // Filter out fixtures we already have match-winner data for
    const uncachedIds = fixtureIds.filter(id => {
      const cached = oddsCache[id]
      // Consider uncached if no data or no match-winner market
      return !cached || !cached.markets.some(m => m.market_slug === 'match-winner')
    })
    if (uncachedIds.length === 0) return

    // Fetch match-winner odds for all fixtures in parallel
    const results = await Promise.allSettled(
      uncachedIds.map(async (fixtureId) => {
        const data = await fetcher<FixtureOddsResponse>(`/odds/fixtures/${fixtureId}?market=match-winner`)
        return { fixtureId, data }
      })
    )

    // Update cache with all successful results
    const newCache: Record<number, FixtureOddsResponse> = {}
    results.forEach((result) => {
      if (result.status === 'fulfilled' && result.value) {
        newCache[result.value.fixtureId] = result.value.data
      }
    })

    if (Object.keys(newCache).length > 0) {
      setOddsCache((prev) => ({ ...prev, ...newCache }))
    }
  }, [oddsCache])

  // Fetch full odds for a single fixture (all markets, for builder)
  const fetchFullFixtureOdds = useCallback(async (fixtureId: number): Promise<void> => {
    // Check if we already have full data (more than just match-winner)
    const cached = oddsCache[fixtureId]
    if (cached && cached.markets.length > 1) return

    const data = await fetcher<FixtureOddsResponse>(`/odds/fixtures/${fixtureId}`)
    setOddsCache((prev) => ({
      ...prev,
      [fixtureId]: data,
    }))
  }, [oddsCache])

  // Force refresh odds - clears cache and fetches fresh data
  const forceRefreshOdds = useCallback(async (fixtureId: number): Promise<void> => {
    // Clear local cache entry first
    setOddsCache((prev) => {
      const next = { ...prev }
      delete next[fixtureId]
      return next
    })

    // Force SWR revalidation (bypasses deduplication)
    const newData = await forceRevalidateOdds(fixtureId)

    // Update cache with fresh data
    if (newData) {
      setOddsCache((prev) => ({
        ...prev,
        [fixtureId]: newData,
      }))
    }
  }, [])

  // Derive loading state for odds
  const oddsLoading = useMemo(() => {
    if (!activeFixtureId) return {}
    return { [activeFixtureId]: activeOddsLoading }
  }, [activeFixtureId, activeOddsLoading])

  // Get best odds for a specific selection
  const getBestOddsForSelection = useCallback(
    (
      fixtureId: number,
      marketSlug: string,
      outcome: string,
      line?: number | null
    ): BestOddsInfo | null => {
      const cached = oddsCache[fixtureId]
      if (!cached) return null

      const market = cached.markets.find((m) => m.market_slug === marketSlug)
      if (!market) return null

      const outcomeData = market.outcomes.find((o) => {
        const outcomeMatch = o.outcome === outcome
        const lineMatch = line == null ? o.line == null : o.line === line
        return outcomeMatch && lineMatch
      })

      if (!outcomeData) return null

      return outcomeData.best
    },
    [oddsCache]
  )

  // Get odds for a specific bookmaker
  const getOddsForBookmaker = useCallback(
    (
      fixtureId: number,
      marketSlug: string,
      outcome: string,
      bookmakerSlug: string,
      line?: number | null
    ): number | null => {
      const cached = oddsCache[fixtureId]
      if (!cached) return null

      const market = cached.markets.find((m) => m.market_slug === marketSlug)
      if (!market) return null

      const outcomeData = market.outcomes.find((o) => {
        const outcomeMatch = o.outcome === outcome
        const lineMatch = line == null ? o.line == null : o.line === line
        return outcomeMatch && lineMatch
      })

      if (!outcomeData) return null

      return outcomeData.by_bookmaker[bookmakerSlug] ?? null
    },
    [oddsCache]
  )

  // Helper to normalize player names for matching (handles different formats)
  const normalizePlayerName = (name: string): string => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z\s]/g, '') // Remove non-letters
      .trim()
  }

  // Find best matching player in outcomes (fuzzy match by name)
  const findPlayerOutcome = (
    outcomes: { outcome: string; player_name: string | null; best: BestOddsInfo; by_bookmaker: Record<string, number> }[],
    playerName: string
  ) => {
    const normalizedSearch = normalizePlayerName(playerName)
    const searchParts = normalizedSearch.split(' ')

    // Try exact match first
    const exactMatch = outcomes.find(
      (o) => o.player_name && normalizePlayerName(o.player_name) === normalizedSearch
    )
    if (exactMatch) return exactMatch

    // Try partial match (last name match or first initial + last name)
    return outcomes.find((o) => {
      if (!o.player_name) return false
      const normalizedOutcome = normalizePlayerName(o.player_name)
      const outcomeParts = normalizedOutcome.split(' ')

      // Check if last names match
      const searchLast = searchParts[searchParts.length - 1]
      const outcomeLast = outcomeParts[outcomeParts.length - 1]
      if (searchLast === outcomeLast) return true

      // Check if search contains outcome or vice versa
      if (normalizedOutcome.includes(normalizedSearch) || normalizedSearch.includes(normalizedOutcome)) return true

      return false
    })
  }

  // Get best odds for a player prop market
  const getBestOddsForPlayer = useCallback(
    (
      fixtureId: number,
      marketSlug: string,
      playerName: string
    ): BestOddsInfo | null => {
      const cached = oddsCache[fixtureId]
      if (!cached) return null

      const market = cached.markets.find((m) => m.market_slug === marketSlug)
      if (!market) return null

      const outcomeData = findPlayerOutcome(market.outcomes, playerName)
      if (!outcomeData) return null

      return outcomeData.best
    },
    [oddsCache]
  )

  // Get odds for a specific bookmaker for a player prop
  const getOddsForPlayerBookmaker = useCallback(
    (
      fixtureId: number,
      marketSlug: string,
      playerName: string,
      bookmakerSlug: string
    ): number | null => {
      const cached = oddsCache[fixtureId]
      if (!cached) return null

      const market = cached.markets.find((m) => m.market_slug === marketSlug)
      if (!market) return null

      const outcomeData = findPlayerOutcome(market.outcomes, playerName)
      if (!outcomeData) return null

      return outcomeData.by_bookmaker[bookmakerSlug] ?? null
    },
    [oddsCache]
  )

  const value = useMemo(
    () => ({
      bookmakers,
      bookmakersLoading,
      preferredBookmaker,
      setPreferredBookmaker,
      oddsFormat,
      setOddsFormat,
      oddsHidden,
      setOddsHidden,
      showValueBadges,
      setShowValueBadges,
      minEdgeThreshold,
      setMinEdgeThreshold,
      getFixtureOddsData,
      fetchFixtureOddsData,
      fetchMatchWinnerOdds,
      fetchFullFixtureOdds,
      forceRefreshOdds,
      oddsLoading,
      formatOdds,
      getBestOddsForSelection,
      getOddsForBookmaker,
      getBestOddsForPlayer,
      getOddsForPlayerBookmaker,
    }),
    [
      bookmakers,
      bookmakersLoading,
      preferredBookmaker,
      setPreferredBookmaker,
      oddsFormat,
      setOddsFormat,
      oddsHidden,
      setOddsHidden,
      showValueBadges,
      setShowValueBadges,
      minEdgeThreshold,
      setMinEdgeThreshold,
      getFixtureOddsData,
      fetchFixtureOddsData,
      fetchMatchWinnerOdds,
      fetchFullFixtureOdds,
      forceRefreshOdds,
      oddsLoading,
      formatOdds,
      getBestOddsForSelection,
      getOddsForBookmaker,
      getBestOddsForPlayer,
      getOddsForPlayerBookmaker,
    ]
  )

  return <OddsContext.Provider value={value}>{children}</OddsContext.Provider>
}

export function useOdds() {
  const ctx = useContext(OddsContext)
  if (!ctx) throw new Error('useOdds must be used within OddsProvider')
  return ctx
}
